import React, { useState } from "react";
import {
  Container,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  getCrmComparison,
  getCrmComparisonMonthly,
  getCrmComparisonQuarterly,
  getCrmComparisonYearly,
} from "../../api";
import { useAnalyticsFetch } from "./hooks/useAnalyticsFetch";
import { FILTER_OPTIONS } from "./components/AnalyticsFilterBar";
import ComparisonCard from "./components/ComparisonCard";
import LeaderboardCard from "./components/LeaderboardCard";
import LineChartCard from "./components/LineChartCard";
import BarChartCard from "./components/BarChartCard";
import { CHART_COLORS } from "./components/chartTheme";
import { formatCurrency } from "./components/formatters";

const MODE_OPTIONS = [
  { value: "period", label: "Period" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

const COMPARE_OPTIONS = [
  { value: "previous_period", label: "vs Previous Period" },
  { value: "previous_year", label: "vs Previous Year" },
  { value: "last_2_years", label: "vs Last 2 Years" },
  { value: "last_3_years", label: "vs Last 3 Years" },
  { value: "last_5_years", label: "vs Last 5 Years" },
];

const LAST_N_YEARS_COLUMNS = [
  { key: "years_back", label: "Years Back" },
  { key: "revenue", label: "Revenue", format: formatCurrency },
  { key: "order_count", label: "Orders" },
  {
    key: "growth_percent",
    label: "Growth %",
    format: (value) => (value === null || value === undefined ? "N/A" : `${value}%`),
  },
  { key: "difference", label: "Difference", format: formatCurrency },
];

const currentYear = new Date().getFullYear();

const Comparison = () => {
  const [mode, setMode] = useState("period");

  // Period mode
  const [periodFilter, setPeriodFilter] = useState("last_30_days");
  const [compareType, setCompareType] = useState("previous_period");
  const isMultiYearCompare = compareType.startsWith("last_");

  // Monthly / Quarterly mode
  const [baseYear, setBaseYear] = useState(currentYear);
  const [seasonalYearsBack, setSeasonalYearsBack] = useState(1);

  // Yearly mode
  const [yearlyYearsBack, setYearlyYearsBack] = useState(5);

  const { data: periodData, loading: periodLoading } = useAnalyticsFetch(
    getCrmComparison,
    [periodFilter, "", "", compareType],
    mode === "period",
  );

  const { data: monthlyData, loading: monthlyLoading } = useAnalyticsFetch(
    getCrmComparisonMonthly,
    [baseYear, seasonalYearsBack],
    mode === "monthly",
  );

  const { data: quarterlyData, loading: quarterlyLoading } = useAnalyticsFetch(
    getCrmComparisonQuarterly,
    [baseYear, seasonalYearsBack],
    mode === "quarterly",
  );

  const { data: yearlyData, loading: yearlyLoading } = useAnalyticsFetch(
    getCrmComparisonYearly,
    [yearlyYearsBack],
    mode === "yearly",
  );

  const seasonalData = mode === "monthly" ? monthlyData : quarterlyData;
  const seasonalLoading = mode === "monthly" ? monthlyLoading : quarterlyLoading;
  const seasonalDatasets = seasonalData?.series
    ? Object.entries(seasonalData.series).map(([year, values], index) => ({
        label: year,
        data: values,
        color: CHART_COLORS[index % CHART_COLORS.length],
      }))
    : [];

  return (
    <Container sx={{ paddingLeft: "0 !important", paddingRight: "0 !important" }}>
      <Box sx={{ marginBottom: "1rem" }}>
        <ToggleButtonGroup
          size="small"
          value={mode}
          exclusive
          onChange={(event, newMode) => {
            if (newMode) {
              setMode(newMode);
            }
          }}
        >
          {MODE_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {mode === "period" && (
        <>
          <Box sx={{ display: "flex", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="comparison-filter-label">Current Period</InputLabel>
              <Select
                labelId="comparison-filter-label"
                label="Current Period"
                value={periodFilter}
                onChange={(event) => setPeriodFilter(event.target.value)}
              >
                {FILTER_OPTIONS.filter((option) => option.value !== "custom").map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="comparison-compare-label">Compare</InputLabel>
              <Select
                labelId="comparison-compare-label"
                label="Compare"
                value={compareType}
                onChange={(event) => setCompareType(event.target.value)}
              >
                {COMPARE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {!isMultiYearCompare && (
            <ComparisonCard
              title="Revenue Comparison"
              current={periodData?.current?.revenue ?? 0}
              previous={periodData?.previous?.revenue ?? 0}
              growthPercent={periodData?.growth_percent}
              difference={periodData?.difference ?? 0}
              trend={periodData?.trend || []}
              loading={periodLoading}
            />
          )}

          {isMultiYearCompare && (
            <LeaderboardCard
              title="Revenue vs Prior Years"
              rows={periodData?.trend || []}
              columns={LAST_N_YEARS_COLUMNS}
              loading={periodLoading}
            />
          )}
        </>
      )}

      {(mode === "monthly" || mode === "quarterly") && (
        <>
          <Box sx={{ display: "flex", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <TextField
              size="small"
              type="number"
              label="Base Year"
              value={baseYear}
              onChange={(event) => setBaseYear(Number(event.target.value))}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="comparison-years-back-label">Years Back</InputLabel>
              <Select
                labelId="comparison-years-back-label"
                label="Years Back"
                value={seasonalYearsBack}
                onChange={(event) => setSeasonalYearsBack(event.target.value)}
              >
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <LineChartCard
            title={mode === "monthly" ? "Monthly Revenue Comparison" : "Quarterly Revenue Comparison"}
            labels={seasonalData?.labels || []}
            datasets={seasonalDatasets}
            loading={seasonalLoading}
            currency
          />
        </>
      )}

      {mode === "yearly" && (
        <>
          <Box sx={{ marginBottom: "1rem" }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="comparison-yearly-years-back-label">Years Back</InputLabel>
              <Select
                labelId="comparison-yearly-years-back-label"
                label="Years Back"
                value={yearlyYearsBack}
                onChange={(event) => setYearlyYearsBack(event.target.value)}
              >
                {[1, 2, 3, 4, 5].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <BarChartCard
            title="Yearly Revenue Comparison"
            labels={yearlyData?.chart?.labels || []}
            series={yearlyData?.chart?.series || []}
            label="Revenue"
            loading={yearlyLoading}
            currency
          />
        </>
      )}
    </Container>
  );
};

export default Comparison;
